import { ApiError, getMessage, request } from './client';
import {
  fuelOptionSchema,
  googleResultSchema,
  stationFuelsSchema,
  stationListSchema,
  stationSchema,
} from '../components/stations/stationSchema';

/**
 * Admin gas stations & fuel prices. Always hits the real backend.
 * Envelope: { Success, Message, Data }. Types: see components/stations/stationSchema.js.
 */

const unexpected = () => new ApiError('Unexpected response from the server.', 200, null);

function parse(schema, data) {
  const result = schema.safeParse(data);
  if (!result.success) throw unexpected();
  return result.data;
}

/**
 * GET admin/stations?page&limit&search&hubType
 * @param {{ page: number, limit: number, search?: string, hubType?: string }} params
 */
export async function getStations({ page, limit, search = '', hubType = 'all' }, { signal } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit), hubType });
  if (search) qs.set('search', search);
  const res = await request(`admin/stations?${qs}`, { signal });
  const envelope = parse(stationListSchema, res?.Data);
  const stations = envelope.stations.flatMap((raw, index) => {
    const row = stationSchema.safeParse(raw);
    if (row.success) return [row.data];
    console.warn(`[stations] skipped malformed station at index ${index}`);
    return [];
  });
  return { ...envelope, stations };
}

/**
 * GET admin/stations/google-search?query&type (Netherlands, max 10).
 * 502 Google unavailable, 504 timeout, 429 quota.
 * @param {string} query  2–200 characters
 * @param {'all'|'fuel'|'ev'} type
 */
export async function searchGoogleStations(query, type = 'all', { signal } = {}) {
  const qs = new URLSearchParams({ query, type });
  const res = await request(`admin/stations/google-search?${qs}`, { signal });
  const results = Array.isArray(res?.Data?.results) ? res.Data.results : [];
  return results.flatMap((raw) => {
    const row = googleResultSchema.safeParse(raw);
    return row.success ? [row.data] : [];
  });
}

/** GET admin/fuel-options[?category]. Only `isActive` options can be priced. */
export async function getFuelOptions(category, { signal } = {}) {
  const qs = category ? `?${new URLSearchParams({ category })}` : '';
  const res = await request(`admin/fuel-options${qs}`, { signal });
  if (!Array.isArray(res?.Data)) throw unexpected();
  return res.Data.flatMap((raw) => {
    const row = fuelOptionSchema.safeParse(raw);
    return row.success ? [row.data] : [];
  });
}

/** GET admin/stations/:placeId/fuels: every option with this station's entry (null = unknown). */
export async function getStationFuels(placeId, { signal } = {}) {
  const res = await request(`admin/stations/${encodeURIComponent(placeId)}/fuels`, { signal });
  return parse(stationFuelsSchema, res?.Data);
}

/**
 * POST admin/stations/fuel-prices: inserts or updates. Also clears a "not sold" flag.
 * @param {string} placeId
 * @param {{ fuelOptionId: number, latestPrice: number }[]} prices  1–20, distinct options
 */
export async function saveStationPrices(placeId, prices) {
  const res = await request('admin/stations/fuel-prices', { method: 'POST', body: { placeId, prices } });
  return { message: getMessage(res), data: parse(stationFuelsSchema, res?.Data) };
}

/** DELETE admin/stations/:placeId/fuels/:fuelOptionId: back to "unknown". 404 if nothing stored. */
export async function resetStationFuel(placeId, fuelOptionId) {
  const res = await request(`admin/stations/${encodeURIComponent(placeId)}/fuels/${encodeURIComponent(fuelOptionId)}`, {
    method: 'DELETE',
  });
  return { message: getMessage(res), data: parse(stationFuelsSchema, res?.Data) };
}

export const stationsApi = { getStations, searchGoogleStations, getFuelOptions, getStationFuels, saveStationPrices, resetStationFuel };
